import React from "react";
import { getAuctionStatus, getStatusColor, isBidInputAvailable } from "../../utils/auction";
import { useAuth } from "../../providers/AuthContext";
import { formatAuctionDate, formatTime12Hour } from "../../utils/dateFormat";
import CountdownTimer from "../CountdownTimer/CountdownTimer";
import { Auction } from "../../api/types";
import { BidPreview } from "../BidPreview/BidPreview";
import BidInput from "../BidInput/BidInput";

export interface AuctionCardProps {
  auction: Auction;
  onBidSubmit: (
    id: string,
    amount: number,
    notes?: string,
    flatRate?: boolean,
  ) => void;
  isSubscriptionActive?: boolean;
  onSelectWinner?: (auctionId: string, company: string) => void;
  onCardClick?: () => void;
  mode?: "default" | "preview";
}

const AuctionCard: React.FC<AuctionCardProps> = ({
  auction,
  onBidSubmit,
  onSelectWinner,
  onCardClick,
  isSubscriptionActive,
  mode = "default",
}) => {
  const {
    id,
    ownerId,
    title,
    origin,
    destination,
    moveDate,
    moveTime,
    bids,
    status,
    currentUserBid,
    winningCompany,
  } = auction;

  const { userId, isCompany } = useAuth();

  const isOwner = userId === ownerId;
  const bidInputAvailable = isBidInputAvailable({
    status,
    isOwner,
    isCompany,
    isSubscriptionActive: isSubscriptionActive ?? false,
    mode,
    endsAt: auction.endsAt,
  });

  const formattedDate = formatAuctionDate(moveDate);

  const lowestBid = bids.sort((a, b) => a.amount - b.amount)[0];

  return (
    <div
      className="bg-white shadow rounded-lg p-4 flex flex-col justify-between overflow-hidden
             cursor-pointer transition hover:shadow-md hover:scale-[1.01] active:opacity-80"
      onClick={onCardClick}
    >
      <div className="min-w-0">
        <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{title}</h3>
        <span
          className={`inline-block text-xs mb-4 px-2 py-1 rounded ${getStatusColor(
            status,
            auction.endsAt
          )} font-semibold`}
        >
          {getAuctionStatus(status, auction.endsAt)}
        </span>

        <p className="text-sm text-gray-500 break-words overflow-wrap-anywhere">
          {origin} → {destination}
        </p>

        <p className="text-sm text-gray-400 mb-3">Move Date: {formattedDate}</p>

        {/* <p className="text-sm text-gray-900 mb-3">
          Distance: {distance ? `${distance} mi` : "N/A"}
        </p> */}

        <CountdownTimer deadline={auction.endsAt} />

        <p className="text-sm text-gray-900 mb-3">
          Move Time: {moveTime ? formatTime12Hour(moveTime) : "N/A"}
        </p>

        {lowestBid && (
          <div className="space-y-2 mb-2">
            <p className="text-sm font-medium text-gray-700">Lowest Bid</p>

            <BidPreview
              bid={lowestBid}
              isOwner={isOwner}
              status={status}
              winningCompany={winningCompany}
              isWinning={lowestBid?.company === winningCompany}
              auctionId={id}
            />
          </div>
        )}
      </div>

      {bidInputAvailable && (
        <div onClick={(e) => e.stopPropagation()}>
          <BidInput
            initialValue={currentUserBid}
            onSubmit={(amount, notes, flatRate) =>
              onBidSubmit(id, amount, notes, flatRate)
            }
          />
        </div>
      )}
    </div>
  );
};

export default AuctionCard;
